// IncomeExpenses.js — Mortgage Matrix Broker CRM
// v7.6.2 — fixes Government income / Other income disappearing on selection
// ---------------------------------------------------------------------------
// ✅ Full time / Part time / Casual income / Government income / Other income
// ✅ Keeps Government income and Other income selectable
// ✅ FIX: row is no longer treated as blank just because hidden fields are empty
// ✅ FIX: selecting Government income / Other income no longer resets page to default state
// ✅ Writes to updateCrmData("income", payload)
// ✅ Adds ownerIndex for Lambda routing
// ✅ Adds Lambda-friendly fields: type, jobTitle, role, dateCommenced, comment,
//    employmentType, employmentBasis, source, details
// ✅ Immediate commit to parent without hydration loop
// ✅ Local Back / Next removed (shared nav handled in BrokerCRM)
// ✅ FIX: no default blank income row is forced into crmData
// ✅ FIX: removing the last row now leaves zero rows instead of recreating a blank row

import React, { useEffect, useMemo, useRef, useState } from "react"

function IncomeExpenses({ data = [], updateCrmData }) {
  const ALLOWED_INCOME_TYPES = [
    "Full time",
    "Part time",
    "Casual income",
    "Government income",
    "Other income",
  ]

  const normaliseWholeDollars = (v) => {
    if (v === null || v === undefined) return ""
    return String(v).replace(/[^\d]/g, "")
  }

  const formatWithCommas = (digits) => {
    const raw = normaliseWholeDollars(digits)
    if (!raw) return ""
    const n = Number(raw)
    if (!Number.isFinite(n)) return ""
    return n.toLocaleString("en-AU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }

  const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`

  const sanitiseIncomeType = (incomeType) => {
    const type = String(incomeType || "").trim()
    return ALLOWED_INCOME_TYPES.includes(type) ? type : "Full time"
  }

  const createRow = (owner = "Applicant 1") => ({
    id: makeId(),
    owner,
    incomeType: "Full time",
    employerName: "",
    roleDescription: "",
    startDate: "",
    category: "Base Salary",
    amount: "",
    frequency: "Annual",
  })

  const getTypeRules = (incomeType) => {
    const type = sanitiseIncomeType(incomeType)

    if (type === "Full time" || type === "Part time" || type === "Casual income") {
      return {
        showEmployerName: true,
        showRoleDetails: true,
        showStartDate: true,
        showCategory: true,
        showFrequency: true,
        employerLabel: "Employer name",
        employerPlaceholder: "Employer name",
        roleLabel: "Role / details",
        rolePlaceholder: "Role / details",
        amountLabel: "Income amount",
        frequencyOptions: ["Annual"],
        defaultFrequency: "Annual",
        defaultCategory: "Base Salary",
      }
    }

    if (type === "Government income") {
      return {
        showEmployerName: false,
        showRoleDetails: false,
        showStartDate: false,
        showCategory: false,
        showFrequency: true,
        employerLabel: "",
        employerPlaceholder: "",
        roleLabel: "",
        rolePlaceholder: "",
        amountLabel: "Government income",
        frequencyOptions: ["Fortnightly", "Weekly", "Monthly", "Annual"],
        defaultFrequency: "Fortnightly",
        defaultCategory: "",
      }
    }

    if (type === "Other income") {
      return {
        showEmployerName: true,
        showRoleDetails: true,
        showStartDate: false,
        showCategory: false,
        showFrequency: true,
        employerLabel: "Income source",
        employerPlaceholder: "Income source",
        roleLabel: "Details",
        rolePlaceholder: "Details",
        amountLabel: "Other income",
        frequencyOptions: ["Monthly", "Fortnightly", "Weekly", "Annual"],
        defaultFrequency: "Monthly",
        defaultCategory: "",
      }
    }

    return {
      showEmployerName: true,
      showRoleDetails: true,
      showStartDate: true,
      showCategory: true,
      showFrequency: true,
      employerLabel: "Employer name",
      employerPlaceholder: "Employer name",
      roleLabel: "Role / details",
      rolePlaceholder: "Role / details",
      amountLabel: "Income amount",
      frequencyOptions: ["Annual"],
      defaultFrequency: "Annual",
      defaultCategory: "Base Salary",
    }
  }

  const sanitiseRowForType = (row) => {
    const safeIncomeType = sanitiseIncomeType(row?.incomeType)
    const rules = getTypeRules(safeIncomeType)

    return {
      ...row,
      id: row?.id || makeId(),
      owner: row?.owner || "Applicant 1",
      incomeType: safeIncomeType,
      employerName: rules.showEmployerName ? String(row?.employerName || "") : "",
      roleDescription: rules.showRoleDetails ? String(row?.roleDescription || "") : "",
      startDate: rules.showStartDate ? String(row?.startDate || "") : "",
      category: rules.showCategory
        ? String(row?.category || rules.defaultCategory || "Base Salary")
        : "",
      amount: normaliseWholeDollars(row?.amount),
      frequency: rules.frequencyOptions.includes(row?.frequency)
        ? row.frequency
        : rules.defaultFrequency,
    }
  }

  const rowsEqual = (a, b) => {
    if (a === b) return true
    if (!Array.isArray(a) || !Array.isArray(b)) return false
    if (a.length !== b.length) return false

    const keys = [
      "id",
      "owner",
      "incomeType",
      "employerName",
      "roleDescription",
      "startDate",
      "category",
      "amount",
      "frequency",
    ]

    for (let i = 0; i < a.length; i++) {
      const x = a[i] || {}
      const y = b[i] || {}
      for (const k of keys) {
        if (String(x[k] ?? "") !== String(y[k] ?? "")) return false
      }
    }
    return true
  }

  const isBlankRow = (row) => {
    const clean = sanitiseRowForType(row)
    const type = sanitiseIncomeType(clean.incomeType)

    if (type === "Government income") {
      return !String(clean.amount || "").trim()
    }

    if (type === "Other income") {
      return (
        !String(clean.employerName || "").trim() &&
        !String(clean.roleDescription || "").trim() &&
        !String(clean.amount || "").trim()
      )
    }

    return (
      !String(clean.employerName || "").trim() &&
      !String(clean.roleDescription || "").trim() &&
      !String(clean.startDate || "").trim() &&
      !String(clean.category || "").trim() &&
      !String(clean.amount || "").trim()
    )
  }

  const mapRowForCrm = (row) => {
    const clean = sanitiseRowForType(row)
    const ownerIndex = clean.owner === "Applicant 2" ? 2 : 1
    const incomeType = sanitiseIncomeType(clean.incomeType)
    const isEmploymentIncome =
      incomeType === "Full time" || incomeType === "Part time" || incomeType === "Casual income"

    return {
      ...clean,

      ownerIndex,
      applicant: clean.owner,
      owner: clean.owner,

      type: incomeType,
      incomeType,
      amount: clean.amount || "",
      frequency: clean.frequency || "Annual",
      dateCommenced: clean.startDate || "",
      comment:
        incomeType === "Government income"
          ? "Government income"
          : incomeType === "Other income"
            ? `${clean.employerName || ""}${clean.roleDescription ? ` - ${clean.roleDescription}` : ""}`.trim()
            : clean.category || "",

      employerName: clean.employerName || "",
      role: clean.roleDescription || "",
      jobTitle: clean.roleDescription || "",
      startDate: clean.startDate || "",
      employmentStatus: "Current",
      employmentType: isEmploymentIncome ? "Payg" : "Other",
      employmentBasis:
        incomeType === "Full time"
          ? "Full Time"
          : incomeType === "Part time"
            ? "Part Time"
            : incomeType === "Casual income"
              ? "Casual"
              : "",

      source: clean.employerName || "",
      details: clean.roleDescription || "",
      category: clean.category || "",
    }
  }

  const initialRows =
    Array.isArray(data) && data.length
      ? data.map((r) => sanitiseRowForType(r))
      : []

  const [activeApplicant, setActiveApplicant] = useState("Applicant 1")
  const [items, setItems] = useState(initialRows)

  const incomeInputRefs = useRef({})
  const lastSentToParentRef = useRef(initialRows)
  const hasHydratedRef = useRef(false)

  useEffect(() => {
    const inbound =
      Array.isArray(data) && data.length
        ? data.map((r) => sanitiseRowForType(r))
        : []

    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true
      setItems(inbound)
      return
    }

    if (rowsEqual(inbound, lastSentToParentRef.current)) return
    if (rowsEqual(inbound, items)) return

    setItems(inbound)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (typeof updateCrmData !== "function") return

    const sanitisedItems = items.map((row) => sanitiseRowForType(row))
    const payload = sanitisedItems
      .filter((row) => !isBlankRow(row))
      .map((row) => mapRowForCrm(row))

    lastSentToParentRef.current = sanitisedItems
    updateCrmData("income", payload)
  }, [items, updateCrmData])

  const addItem = () => {
    setItems((prev) => [...prev, createRow(activeApplicant)])
  }

  const removeItem = (id) => {
    setItems((prev) => prev.filter((row) => row.id !== id))
  }

  const updateItem = (id, field, value) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row

        let next = { ...row, [field]: value }

        if (field === "amount") {
          next.amount = normaliseWholeDollars(value)
        }

        if (field === "incomeType") {
          const safeType = sanitiseIncomeType(value)
          const rules = getTypeRules(safeType)

          next = {
            ...next,
            incomeType: safeType,
            frequency: rules.defaultFrequency,
            category: rules.showCategory ? rules.defaultCategory : "",
            startDate: rules.showStartDate ? next.startDate || "" : "",
            employerName: rules.showEmployerName ? next.employerName || "" : "",
            roleDescription: rules.showRoleDetails ? next.roleDescription || "" : "",
          }

          if (!rules.showStartDate) next.startDate = ""
          if (!rules.showCategory) next.category = ""
          if (!rules.showEmployerName) next.employerName = ""
          if (!rules.showRoleDetails) next.roleDescription = ""
        }

        const rules = getTypeRules(next.incomeType)

        if (field === "frequency" && !rules.frequencyOptions.includes(value)) {
          next.frequency = rules.defaultFrequency
        }

        return sanitiseRowForType(next)
      })
    )
  }

  const visibleItems = items.filter((row) => row.owner === activeApplicant)

  const incomeTypes = [
    "Full time",
    "Part time",
    "Casual income",
    "Government income",
    "Other income",
  ]

  const categories = ["Base Salary", "Bonus", "Commission", "Other"]

  const ui = useMemo(() => {
    const ACCENT = "#4EDCF6"
    const DARK = "#111827"
    const BORDER = "#E5E7EB"
    const INPUT_BORDER = "#334155"
    const SHADOW = "0 10px 24px rgba(78, 220, 246, 0.22)"

    return {
      h2: { marginBottom: 18, fontSize: 28, fontWeight: 700, color: DARK },

      label: {
        display: "block",
        marginBottom: 8,
        fontWeight: 600,
        color: DARK,
        fontSize: 15,
      },

      input: {
        width: "100%",
        height: 50,
        padding: "10px 12px",
        borderRadius: 14,
        border: `1px solid rgba(17,17,17,0.18)`,
        background: "#fff",
        fontSize: 16,
        fontWeight: 600,
        outline: "none",
        boxShadow: "none",
      },

      switchWrap: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 14,
        marginBottom: 18,
      },

      switchBtn: (active) => ({
        width: "100%",
        minHeight: 60,
        padding: "0 18px",
        borderRadius: 12,
        border: active ? "2px solid #141414" : `1.5px solid rgba(17,17,17,0.12)`,
        background: active ? "rgba(17,17,17,0.04)" : "#ffffff",
        cursor: "pointer",
        fontWeight: 650,
        fontSize: 18,
        color: DARK,
        boxShadow: active ? "0 8px 18px rgba(17,17,17,0.06)" : "none",
        transition: "all 0.16s ease",
      }),

      card: {
        background: "#ffffff",
        padding: 20,
        borderRadius: 16,
        border: `1px solid rgba(17,17,17,0.08)`,
        boxShadow: "0 10px 24px rgba(20,20,20,0.05)",
        marginBottom: 16,
      },

      fieldGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 18,
        alignItems: "end",
      },

      primaryBtn: {
        padding: "12px 18px",
        background: ACCENT,
        border: "none",
        borderRadius: 12,
        cursor: "pointer",
        fontWeight: 650,
        boxShadow: SHADOW,
      },

      removeBtn: {
        border: "none",
        background: "transparent",
        fontSize: 16,
        color: "#DC2626",
        cursor: "pointer",
        fontWeight: 650,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      },

      adornWrap: {
        position: "relative",
        width: "100%",
        height: 50,
      },

      adornLeft: {
        position: "absolute",
        left: 14,
        top: "50%",
        transform: "translateY(-50%)",
        fontSize: 16,
        fontWeight: 700,
        color: DARK,
        opacity: 0.8,
        pointerEvents: "none",
        lineHeight: 1,
      },

      inputWithLeftAdorn: {
        width: "100%",
        height: 50,
        padding: "10px 12px 10px 34px",
        borderRadius: 14,
        border: `1px solid rgba(17,17,17,0.18)`,
        background: "#fff",
        fontSize: 16,
        fontWeight: 600,
        outline: "none",
        boxShadow: "none",
      },

      emptyState: {
        padding: "20px 18px",
        borderRadius: 14,
        border: `1.5px dashed rgba(17,17,17,0.14)`,
        background: "#fff",
        color: DARK,
        fontWeight: 600,
        marginBottom: 16,
      },
    }
  }, [])

  return (
    <div style={{ padding: 8, maxWidth: 980 }}>
      <h2 style={ui.h2}>Income</h2>

      <div style={ui.switchWrap}>
        <button
          type="button"
          style={ui.switchBtn(activeApplicant === "Applicant 1")}
          onClick={() => setActiveApplicant("Applicant 1")}
        >
          Applicant 1
        </button>

        <button
          type="button"
          style={ui.switchBtn(activeApplicant === "Applicant 2")}
          onClick={() => setActiveApplicant("Applicant 2")}
        >
          Applicant 2
        </button>
      </div>

      {!visibleItems.length && (
        <div style={ui.emptyState}>
          No income items added for {activeApplicant}.
        </div>
      )}

      {visibleItems.map((item) => {
        const rules = getTypeRules(item.incomeType)

        return (
          <div key={item.id} style={ui.card}>
            <div style={ui.fieldGrid}>
              <div>
                <label style={ui.label}>Applicant</label>
                <select
                  value={item.owner}
                  onChange={(e) => updateItem(item.id, "owner", e.target.value)}
                  style={ui.input}
                >
                  <option value="Applicant 1">Applicant 1</option>
                  <option value="Applicant 2">Applicant 2</option>
                </select>
              </div>

              <div>
                <label style={ui.label}>Income type</label>
                <select
                  value={item.incomeType}
                  onChange={(e) => updateItem(item.id, "incomeType", e.target.value)}
                  style={ui.input}
                >
                  {incomeTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {rules.showEmployerName && (
                <div>
                  <label style={ui.label}>{rules.employerLabel}</label>
                  <input
                    type="text"
                    placeholder={rules.employerPlaceholder}
                    value={item.employerName}
                    onChange={(e) => updateItem(item.id, "employerName", e.target.value)}
                    style={ui.input}
                  />
                </div>
              )}

              {rules.showStartDate && (
                <div>
                  <label style={ui.label}>Current employment start date</label>
                  <input
                    type="date"
                    value={item.startDate}
                    onChange={(e) => updateItem(item.id, "startDate", e.target.value)}
                    style={ui.input}
                  />
                </div>
              )}

              {rules.showRoleDetails && (
                <div>
                  <label style={ui.label}>{rules.roleLabel}</label>
                  <input
                    type="text"
                    placeholder={rules.rolePlaceholder}
                    value={item.roleDescription}
                    onChange={(e) => updateItem(item.id, "roleDescription", e.target.value)}
                    style={ui.input}
                  />
                </div>
              )}

              {rules.showCategory && (
                <div>
                  <label style={ui.label}>Category</label>
                  <select
                    value={item.category || "Base Salary"}
                    onChange={(e) => updateItem(item.id, "category", e.target.value)}
                    style={ui.input}
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label style={ui.label}>{rules.amountLabel}</label>
                <div style={ui.adornWrap}>
                  <span style={ui.adornLeft}>$</span>
                  <input
                    ref={(el) => {
                      if (el) incomeInputRefs.current[item.id] = el
                    }}
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={formatWithCommas(item.amount)}
                    onChange={(e) => {
                      const raw = normaliseWholeDollars(e.target.value)
                      updateItem(item.id, "amount", raw)

                      requestAnimationFrame(() => {
                        const el = incomeInputRefs.current[item.id]
                        if (!el) return
                        const len = el.value.length
                        el.setSelectionRange(len, len)
                      })
                    }}
                    style={ui.inputWithLeftAdorn}
                  />
                </div>
              </div>

              {rules.showFrequency && (
                <div>
                  <label style={ui.label}>Frequency</label>
                  <select
                    value={item.frequency}
                    onChange={(e) => updateItem(item.id, "frequency", e.target.value)}
                    style={ui.input}
                  >
                    {rules.frequencyOptions.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div style={{ marginTop: 16, textAlign: "right" }}>
              <button type="button" onClick={() => removeItem(item.id)} style={ui.removeBtn}>
                ❌ Remove
              </button>
            </div>
          </div>
        )
      })}

      <button type="button" onClick={addItem} style={ui.primaryBtn}>
        + Add Income Item
      </button>
    </div>
  )
}

export default IncomeExpenses